import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useMemo, useCallback, memo, useRef, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { useKeyboardFocusState } from '../../utils/useKeyboardFocusState';
import { Icon } from '../Icon/index';
import { toggleLabelTypes, toggleSizes, outerLabelPositions, } from './constants';
const variationStyles = {
    [toggleSizes.large]: {
        borderRadius: styleUtils.rem(24),
        fontStyle: theme.typeStyles.captionLargeStyles,
        height: styleUtils.rem(48),
        iconSize: 'medium',
        narrowWidth: styleUtils.rem(88),
        spacing: theme.spacing.x4,
        wideWidth: styleUtils.rem(100),
    },
    [toggleSizes.medium]: {
        borderRadius: styleUtils.rem(16),
        fontStyle: theme.typeStyles.captionMediumStyles,
        height: styleUtils.rem(32),
        iconSize: 'small',
        narrowWidth: styleUtils.rem(56),
        spacing: theme.spacing.x3,
        wideWidth: styleUtils.rem(72),
    },
    [toggleSizes.small]: {
        borderRadius: styleUtils.rem(12),
        fontStyle: theme.typeStyles.captionSmallStyles,
        height: styleUtils.rem(24),
        iconSize: 'extraSmall',
        narrowWidth: styleUtils.rem(40),
        spacing: theme.spacing.x2,
        wideWidth: styleUtils.rem(56),
    },
};
const iconContainerStyles = css `
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  position: absolute;
  width: 100%;
`;
const labelStyles = css `
  align-items: center;
  cursor: pointer;
  display: flex;
  user-select: none;
`;
const outerLabelStyles = {
    [outerLabelPositions.left]: css `
    margin-right: ${theme.spacing.x3};
  `,
    [outerLabelPositions.right]: css `
    margin-left: ${theme.spacing.x3};
    order: 2;
  `,
};
const defaultProps = {
    ariaChecked: undefined,
    ariaDescribedBy: undefined,
    ariaLabel: undefined,
    ariaLabelledBy: undefined,
    checked: false,
    disabled: false,
    id: '',
    labelOffIconType: 'invisible',
    labelOnIconType: 'visible',
    labelType: toggleLabelTypes.none,
    onChange: () => { },
    outerLabelPosition: outerLabelPositions.left,
    outerLabelText: '',
    size: toggleSizes.medium,
};
const Toggle = ({ ariaChecked = defaultProps.ariaChecked, ariaDescribedBy = defaultProps.ariaDescribedBy, ariaLabel = defaultProps.ariaLabel, ariaLabelledBy = defaultProps.ariaLabelledBy, checked = defaultProps.checked, disabled = defaultProps.disabled, id = defaultProps.id, labelOffIconType = defaultProps.labelOffIconType, labelOnIconType = defaultProps.labelOnIconType, labelType = defaultProps.labelType, name, onChange = defaultProps.onChange, outerLabelPosition = defaultProps.outerLabelPosition, outerLabelText = defaultProps.outerLabelText, size = defaultProps.size, }) => {
    const inputRef = useRef(null);
    const isFocused = useKeyboardFocusState(inputRef);
    const outerLabelTextStyles = useMemo(() => (css `
        ${outerLabelStyles[outerLabelPosition]}
        ${variationStyles[size].fontStyle}
      `), [
        outerLabelPosition,
        size,
    ]);
    const inputContainerWidth = useMemo(() => (labelType === toggleLabelTypes.none
        ? variationStyles[size].narrowWidth
        : variationStyles[size].wideWidth), [
        size,
        labelType,
    ]);
    const inputContainerStyles = useMemo(() => (css `
        align-items: center;
        display: flex;
        height: ${variationStyles[size].height};
        position: relative;
        width: ${inputContainerWidth};
      `), [
        size,
        inputContainerWidth,
    ]);
    const backgroundStyles = useMemo(() => (css `
        background-color: ${checked ? theme.colors.accent700 : theme.colors.gray100};
        border: ${styleUtils.rem(1)} solid ${checked ? theme.colors.accent700 : theme.colors.gray400};

        ${disabled && css `
          background-color: ${checked ? theme.colors.gray400 : theme.colors.gray300};
          border-color: ${checked ? theme.colors.gray400 : theme.colors.gray300};
        `}
        border-radius: ${variationStyles[size].borderRadius};
        height: calc(100% - ${styleUtils.rem(2)});
        position: absolute;
        width: calc(100% - ${styleUtils.rem(2)});

        ${isFocused && styleUtils.focusVisibleStyles};
      `), [
        checked,
        disabled,
        isFocused,
        size,
    ]);
    const knobStyles = useMemo(() => (css `
        ${styleUtils.transition('left')}
        background-color: ${checked ? theme.colors.gray100 : theme.colors.gray400};

        ${disabled && css `
          background-color: ${checked ? theme.colors.gray100 : theme.colors.gray400};
        `}
        border-radius: 50%;
        height: calc(${variationStyles[size].height} - ${theme.spacing.x2});
        left: ${checked ? `calc(100% - (${variationStyles[size].height} - ${theme.spacing.x2}) - ${theme.spacing.base}) ` : theme.spacing.base};
        position: relative;
        width: calc(${variationStyles[size].height} - ${theme.spacing.x2});
      `), [
        checked,
        disabled,
        size,
    ]);
    const iconStyles = useMemo(() => (css `
        ${variationStyles[size].fontStyle}
        ${styleUtils.transition('all')}
        align-items: center;
        display: inline-flex;
        text-align: center;
        text-transform: uppercase;
      `), [
        size,
    ]);
    const iconOffStyles = useMemo(() => (css `
        ${iconStyles}
        color: ${disabled ? theme.colors.gray500 : theme.colors.gray700};
        margin-right: ${variationStyles[size].spacing};
        opacity: ${checked ? '0' : '1'};
      `), [
        size,
        checked,
        disabled,
        iconStyles,
    ]);
    const iconOnStyles = useMemo(() => (css `
        ${iconStyles}
        color: ${theme.colors.gray100};
        margin-left: ${variationStyles[size].spacing};
        opacity: ${checked ? '1' : '0'};
      `), [
        size,
        checked,
        iconStyles,
    ]);
    const inputId = useMemo(() => (id || `toggle-id_${name}`), [
        id,
        name,
    ]);
    const handleOnChange = useCallback((event) => {
        if (!disabled) {
            onChange(event);
        }
    }, [
        onChange,
        disabled,
    ]);
    return (_jsxs("label", Object.assign({ css: labelStyles, htmlFor: inputId }, { children: [outerLabelText
                && (_jsx("div", Object.assign({ css: outerLabelTextStyles }, { children: outerLabelText }))), _jsxs("div", Object.assign({ css: inputContainerStyles }, { children: [_jsx("input", { "aria-checked": ariaChecked, "aria-describedby": ariaDescribedBy, "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : outerLabelText, "aria-labelledby": ariaLabelledBy, css: styleUtils.visuallyHidden, defaultChecked: checked, disabled: disabled, id: inputId, name: name, onChange: handleOnChange, ref: inputRef, type: "checkbox" }), _jsx("div", { css: backgroundStyles }), labelType !== toggleLabelTypes.none && (_jsxs("div", Object.assign({ css: iconContainerStyles }, { children: [_jsx("span", Object.assign({ "aria-hidden": "true", className: 'toggle-icon', css: iconOnStyles }, { children: labelType === toggleLabelTypes.text
                                    ? 'on'
                                    : (_jsx(Icon, { size: variationStyles[size].iconSize, type: labelOnIconType })) })), _jsx("span", Object.assign({ "aria-hidden": "true", className: 'toggle-icon', css: iconOffStyles }, { children: labelType === toggleLabelTypes.text
                                    ? 'off'
                                    : (_jsx(Icon, { size: variationStyles[size].iconSize, type: labelOffIconType })) }))] }))), _jsx("div", { css: knobStyles })] }))] })));
};
const MemoizedToggle = memo(Toggle);
export { MemoizedToggle as Toggle };
// MC-1068 Temporary hack for Storybook.
export default Toggle;
